import clsx from "clsx";

import { useUser } from "@components/user-context";
import { TertiaryButton } from "@components/v2";
import { type SalesMetricFilterInput } from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";

import { useSalesV4Context } from "./hooks";

const METRIC_FILTER_OPERATOR: Record<string, string> = {
  gt: ">",
  gte: ">=",
  eq: "=",
  lt: "<",
  lte: "<=",
};

interface Props {
  metricFilters: SalesMetricFilterInput[];
  onClear: () => void;
  onModalOpen: () => void;
}

export const MetricFilters = ({
  metricFilters,
  onClear,
  onModalOpen,
}: Props) => {
  return (
    <>
      {metricFilters.length > 0 && (
        <MetricButton
          metricFilters={metricFilters}
          onClear={onClear}
          onModalOpen={onModalOpen}
        />
      )}
      {metricFilters.length === 0 && (
        <TertiaryButton onClick={onModalOpen}>
          FILTER
          <i className="fa-regular fa-filter"></i>
        </TertiaryButton>
      )}
    </>
  );
};

const MetricButton = ({
  metricFilters,
  onClear,
  onModalOpen,
}: {
  metricFilters: SalesMetricFilterInput[];
  onClear: () => void;
  onModalOpen: () => void;
}) => {
  const { user } = useUser();
  const { metrics } = useSalesV4Context();

  const { metricId, operator, value } = metricFilters[0];
  const metric = metrics.find((e) => e.id === metricId);
  const formattedValue = formatting.formatMetric(
    value,
    metric?.format ?? "decimal",
    {
      code: user.currencies.currentCurrency,
      precision: "approximate",
    },
  );

  return (
    <div
      className={clsx(
        "bg-v2-primary border border-v2-light rounded-md px-2 py-1",
        "text-v2-action text-sm font-semibold tracking-wide",
        "flex items-center cursor-pointer",
        "hover:bg-v2-secondary hover:text-v2-action-hover",
      )}
      onClick={onModalOpen}
    >
      Filter: {metric?.name} {METRIC_FILTER_OPERATOR[operator]} {formattedValue}{" "}
      {metricFilters.length > 1 && `(${metricFilters.length - 1}+)`}
      <i
        className={clsx(
          "fa-regular fa-x text-2xs ml-1 mt-0.5 font-bold rounded-full flex-center h-4.5 w-4.5",
          "hover:bg-v2-overlay-action hover:text-v2-inverse",
        )}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      ></i>
    </div>
  );
};
